// The portal frame: a floating top bar with grouped menus on desktop, a full height menu on phones,
// and page search on Cmd/Ctrl+K. Pages render inside <main id="main">.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CaretDown, MagnifyingGlass, ShieldCheck, SignOut } from '@phosphor-icons/react';
import { SectionContext, installPointerLight } from 'design';
import CommandMenu from './CommandMenu';
import { ADMIN_ITEM, activeItem, allVisibleItems, canSee, visibleGroups } from './navConfig';
import './shell.css';

function initialsFor(user) {
  return String(user?.name || user?.email || 'RC')
    .trim().split(/\s+/).slice(0, 2).map((part) => part[0] || '').join('').toUpperCase();
}

function Avatar({ user, size = 32 }) {
  const [failed, setFailed] = useState(false);
  if (user?.picture && !failed) {
    return <img className="rc-avatar" src={user.picture} alt="" width={size} height={size} referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
  }
  return <span className="rc-avatar rc-avatar--initials" aria-hidden="true">{initialsFor(user)}</span>;
}

function MenuToggle({ open, onClick }) {
  return (
    <button type="button" className="rc-bar__icon rc-menu-toggle" aria-expanded={open} aria-controls="rc-phone-menu" aria-label={open ? 'Close menu' : 'Open menu'} onClick={onClick}>
      <span className="rc-menu-toggle__lines" data-open={open || undefined}>
        <span />
        <span />
      </span>
    </button>
  );
}

function NavItemLink({ item, current, badge, onNavigate, style }) {
  const Icon = item.icon;
  return (
    <Link to={item.path} className="rc-nav-item" aria-current={current ? 'page' : undefined} onClick={onNavigate} style={style}>
      <span className="rc-nav-tile">{Icon ? <Icon size={18} aria-hidden="true" /> : null}</span>
      <span className="rc-nav-item__text">
        <span className="rc-nav-item__label">
          {item.label}
          {current ? <span className="rc-dot" aria-hidden="true" /> : null}
          {badge}
        </span>
        <span className="rc-nav-item__desc">{item.description}</span>
      </span>
    </Link>
  );
}

function GroupMenu({ group, open, active, currentPath, onOpen, onClose, renderBadge }) {
  const trigger = useRef(null);
  const panel = useRef(null);
  const hoverTimer = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') { onClose(); trigger.current?.focus(); }
    };
    const onPointer = (event) => {
      if (!trigger.current?.contains(event.target) && !panel.current?.contains(event.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('pointerdown', onPointer); };
  }, [open, onClose]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  const hover = (entering) => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => (entering ? onOpen() : onClose()), entering ? 90 : 220);
  };

  return (
    <div className="rc-group" onPointerEnter={(event) => { if (event.pointerType === 'mouse') hover(true); }} onPointerLeave={(event) => { if (event.pointerType === 'mouse') hover(false); }}>
      <button
        ref={trigger}
        type="button"
        className="rc-bar__link"
        aria-expanded={open}
        aria-controls={`rc-group-${group.id}`}
        data-active={active || undefined}
        onClick={() => (open ? onClose() : onOpen())}
      >
        {group.label}
        <CaretDown size={14} aria-hidden="true" className="rc-bar__caret" />
      </button>
      {open ? (
        <div ref={panel} id={`rc-group-${group.id}`} className="rc-dropdown">
          {group.items.map((item) => (
            <NavItemLink key={item.path} item={item} current={item.path === currentPath} badge={renderBadge(item)} onNavigate={onClose} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function UserMenu({ user, onLogout, currentPath }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!wrap.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('pointerdown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('pointerdown', onPointer); window.removeEventListener('keydown', onKey); };
  }, [open]);
  useEffect(() => { setOpen(false); }, [currentPath]);
  return (
    <div className="rc-user" ref={wrap}>
      <button type="button" className="rc-bar__avatar" aria-expanded={open} aria-haspopup="true" aria-label={`Account: ${user?.name || user?.email || 'Staff'}`} onClick={() => setOpen((value) => !value)}>
        <Avatar user={user} />
      </button>
      {open ? (
        <div className="rc-menu rc-user__menu">
          <div className="rc-user__who">
            <strong>{user?.name || 'Staff'}</strong>
            <span>{user?.email}</span>
          </div>
          {canSee(user, ADMIN_ITEM) ? (
            <Link className="rc-menu-item" to={ADMIN_ITEM.path} aria-current={currentPath === ADMIN_ITEM.path ? 'page' : undefined}>
              <ShieldCheck size={18} aria-hidden="true" />
              {ADMIN_ITEM.label}
            </Link>
          ) : null}
          <button type="button" className="rc-menu-item" onClick={onLogout}>
            <SignOut size={18} aria-hidden="true" />
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PhoneMenu({ open, groups, user, currentPath, onClose, onLogout, renderBadge }) {
  const panel = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = 'hidden';
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const timer = window.setTimeout(() => panel.current?.querySelector('a, button')?.focus({ preventScroll: true }), 60);
    return () => { root.style.overflow = overflow; window.removeEventListener('keydown', onKey); window.clearTimeout(timer); };
  }, [open, onClose]);
  if (!open) return null;
  const items = groups.flatMap((group) => group.items);
  const total = items.length + 2;
  const step = Math.min(60, Math.floor(400 / Math.max(total, 1)));
  let order = 0;
  const delay = () => ({ animationDelay: `${(order++) * step}ms` });
  return (
    <div className="rc-phone-menu" id="rc-phone-menu" role="dialog" aria-modal="true" aria-label="Menu" ref={panel}>
      <nav className="rc-phone-menu__inner" aria-label="Portal">
        <Link to="/dashboard" className="rc-phone-menu__home" aria-current={currentPath === '/dashboard' ? 'page' : undefined} onClick={onClose} style={delay()}>
          Home
          {currentPath === '/dashboard' ? <span className="rc-dot" aria-hidden="true" /> : null}
        </Link>
        {groups.map((group) => (
          <section key={group.id} className="rc-phone-menu__group">
            <h2 className="rc-phone-menu__heading" style={delay()}>{group.label}</h2>
            {group.items.map((item) => (
              <NavItemLink key={item.path} item={item} current={item.path === currentPath} badge={renderBadge(item)} onNavigate={onClose} style={delay()} />
            ))}
          </section>
        ))}
        <section className="rc-phone-menu__group rc-phone-menu__account" style={delay()}>
          <div className="rc-user__who">
            <Avatar user={user} />
            <span className="rc-stack" style={{ gap: 0 }}>
              <strong>{user?.name || 'Staff'}</strong>
              <span>{user?.email}</span>
            </span>
          </div>
          <div className="rc-row">
            {canSee(user, ADMIN_ITEM) ? <Link className="rc-btn" to={ADMIN_ITEM.path} onClick={onClose}><ShieldCheck size={18} aria-hidden="true" />{ADMIN_ITEM.label}</Link> : null}
            <button type="button" className="rc-btn" onClick={onLogout}><SignOut size={18} aria-hidden="true" />Log out</button>
          </div>
        </section>
      </nav>
    </div>
  );
}

export default function AppFrame({ user, onLogout, renderBadge = () => null, children }) {
  const location = useLocation();
  const groups = useMemo(() => visibleGroups(user), [user]);
  const searchable = useMemo(() => allVisibleItems(user), [user]);
  const current = activeItem(location.pathname, searchable);
  const currentPath = current?.path || location.pathname;
  // The red label above each page title: the menu section this page sits in
  const section = current?.group && current.group !== 'Home' ? current.group : 'RandCards staff';
  const [openGroup, setOpenGroup] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => { setOpenGroup(null); setPhoneOpen(false); setSearchOpen(false); }, [location.pathname]);

  // Sections rise in only while a page opens. About a second later the page counts as settled,
  // so anything that appears after that (a banner, an update, a dialog) shows at once.
  const [settled, setSettled] = useState(false);
  useLayoutEffect(() => {
    setSettled(false);
    const timer = window.setTimeout(() => setSettled(true), 1000);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  // The red light that follows the pointer across tiles
  useEffect(() => installPointerLight(), []);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPhoneOpen(false);
        setSearchOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const closeGroup = useCallback(() => setOpenGroup(null), []);
  const closePhone = useCallback(() => setPhoneOpen(false), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  return (
    <div className="rc-app">
      <a className="rc-skip" href="#main">Skip to content</a>
      <header className="rc-bar" data-menu-open={phoneOpen || undefined}>
        <Link to="/dashboard" className="rc-bar__logo" aria-label="RandCards home" aria-current={location.pathname === '/dashboard' ? 'page' : undefined}>
          <img src="/randcards-logo.png" alt="" width="49" height="28" />
        </Link>
        <nav className="rc-bar__groups" aria-label="Portal">
          {groups.map((group) => (
            <GroupMenu
              key={group.id}
              group={group}
              open={openGroup === group.id}
              active={group.items.some((item) => item.path === currentPath)}
              currentPath={currentPath}
              onOpen={() => setOpenGroup(group.id)}
              onClose={closeGroup}
              renderBadge={renderBadge}
            />
          ))}
        </nav>
        <div className="rc-bar__end">
          <button type="button" className="rc-bar__search" onClick={() => setSearchOpen(true)} aria-label="Search pages">
            <MagnifyingGlass size={18} aria-hidden="true" />
            <span className="rc-bar__search-text">Search</span>
            <kbd className="rc-kbd">⌘K</kbd>
          </button>
          <UserMenu user={user} onLogout={onLogout} currentPath={currentPath} />
          <MenuToggle open={phoneOpen} onClick={() => setPhoneOpen((value) => !value)} />
        </div>
      </header>
      <PhoneMenu open={phoneOpen} groups={groups} user={user} currentPath={currentPath} onClose={closePhone} onLogout={onLogout} renderBadge={renderBadge} />
      <CommandMenu open={searchOpen} onClose={closeSearch} items={searchable} />
      <main id="main" className="rc-main" tabIndex={-1} data-settled={settled || undefined}>
        <SectionContext.Provider value={section}>
          {children}
        </SectionContext.Provider>
      </main>
    </div>
  );
}
